import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { InvitationPreviewDTO, PagedDTO, WorkshopBody, WorkshopDetailDTO, WorkshopRowDTO } from "../../models/WorkshopDTO";

const base = (campusId: number) => `/admin/campus/${campusId}/workshops`;

/** Multi-día: una sesión del taller (GET _details → sessions[]). Fecha "YYYY-MM-DD", horas "HH:MM". */
export interface WorkshopSessionDTO {
	id: number;
	date: string;
	startTime: string;
	endTime: string | null;
	classroomId: number | null;
	classroomName: string | null;
	mentorId: number | null;
	mentorName: string | null;
	notes: string | null;
	attendance: { attended: number; total: number; marked?: number };
}

/** Lista: + sessionsCount (0 = taller de un solo día). */
export type WorkshopRowMultiDTO = WorkshopRowDTO & { sessionsCount?: number } & WorkshopClarosFields;

/** Módulos Claros (2026-11-01): pestañas por fechas. */
export type WorkshopTab = "proximos" | "enCurso" | "terminados" | "anulados";
export interface WorkshopClarosFields {
	tab?: WorkshopTab;
	isPersonalizado?: boolean;
	/** Personalizado: la alumna (la fila lleva su nombre). */
	studentName?: string | null;
	collected?: number;
	pending?: number;
	sessionDates?: string[];
	sameTime?: boolean;
	timeFrom?: string | null;
	timeTo?: string | null;
	createdAt?: string | null;
	createdByName?: string | null;
}
export type WorkshopTabCounts = Record<WorkshopTab, number>;

/** «Qué Va a Pasar» (GET _preview): lo que haría la acción, por persona; el servidor calcula los montos. */
export interface WorkshopPreviewDTO {
	action: "inscribir" | "anular" | "borrarDia";
	workshopId: number;
	workshopName: string;
	currencySymbol: string;
	people: { studentId: number; name: string; lines: string[]; amount: number; blocked: boolean }[];
	total: number;
	blocked: number;
	ok: number;
	notes: string[];
}

/** Los días de taller de la maestra (Asistencias). */
export interface MentorWorkshopSessionDTO {
	id: number; workshopId: number; workshopName: string; date: string; startTime: string; endTime: string | null; classroomName: string | null;
	students: { attendeeId: number; studentId: number; name: string; identityCode: string | null; attended: boolean; marked: boolean }[];
}
export interface MentorWorkshopSessionsDTO { today: string; sessions: MentorWorkshopSessionDTO[]; }

export interface WorkshopSessionBody {
	date?: string;
	startTime?: string;
	endTime?: string | null;
	classroomId?: number | null;
	mentorId?: number | null;
	notes?: string | null;
}

/** Pago de maestras del taller (v2, 2026-10-23): horas de cada una, su tarifa y lo que se le paga. */
export interface MentorPayRowDTO { mentorId: number; name: string; payType: "planta" | "hora" | null; hours: number; extraHours: number; rate: number | null; amount: number | null; note: string; }
export interface WorkshopMentorPayDTO { rows: (MentorPayRowDTO & { configured?: boolean })[]; total: number; incomplete: boolean; hourlyRateOverride: number | null; configured?: boolean; }

/** Forma de pago de una maestra (ficha de Personal). */
export interface MentorPayConfigDTO { userId: number; payType: "planta" | "hora"; payTypeLabel: string; monthlySalary: number | null; monthlyHoursCap: number | null; hourlyRate: number | null; currency: { id: number; code: string | null; symbol: string | null } | null; }
export interface MentorPayMonthDTO { month: string; hours: number; cap: number | null; extraHours: number | null; }

/** El detalle con sesiones: cada inscrita trae sessions[{sessionId, attended}]. */
export type WorkshopMultiDetailDTO = Omit<WorkshopDetailDTO, "attendees"> & WorkshopClarosFields & {
	sessions?: WorkshopSessionDTO[];
	mentorPay?: WorkshopMentorPayDTO | null;
	sessionsCount?: number;
	attendees: (WorkshopDetailDTO["attendees"][number] & { sessions?: { sessionId: number; attended: boolean; marked?: boolean }[]; requestId?: number | null; soldBy?: string | null })[];
};

/** Talleres (FASE 4). */
export class WorkshopsService {
	list = async (campusId: number, f: { status?: string; q?: string; from?: string; to?: string; page?: number; perPage?: number } = {}): Promise<AxiosResponse<PagedDTO<WorkshopRowDTO>>> => {
		const params: { [k: string]: string | number } = {};
		Object.entries(f).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") params[k] = v as any; });
		return axiosInstance.get<PagedDTO<WorkshopRowDTO>>(base(campusId), { params });
	}

	details = async (campusId: number, id: number): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.get<WorkshopDetailDTO>(`${base(campusId)}/_details`, { params: { id } });

	create = async (campusId: number, body: WorkshopBody): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.post<WorkshopDetailDTO>(base(campusId), body);

	/** Anular anula los cobros sin abonos; con abonos 422. */
	update = async (campusId: number, body: WorkshopBody & { id: number }): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.put<WorkshopDetailDTO>(base(campusId), body);

	/** 422: lleno, repetida, alumna de otro plantel. */
	addAttendee = async (campusId: number, body: { workshopId: number; studentId: number; charge?: boolean; price?: number; priceNote?: string; note?: string }): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.post<WorkshopDetailDTO>(`${base(campusId)}/_attendees`, body);

	/** Inscribe varias de una vez (R2): las repetidas, de otro plantel o sin cupo vienen en skipped con su motivo. */
	addAttendeesBulk = async (campusId: number, workshopId: number, studentIds: number[], charge?: boolean): Promise<AxiosResponse<{ workshopId: number; added: number; skipped: { studentId: number; reason: string }[] }>> =>
		axiosInstance.post(`${base(campusId)}/_attendeesBulk`, { workshopId, studentIds, charge });

	setAttendance = async (campusId: number, attendeeId: number, attended: boolean): Promise<AxiosResponse<any>> =>
		axiosInstance.put(`${base(campusId)}/_attendance`, { attendeeId, attended });

	/** Con abonos 422. */
	removeAttendee = async (campusId: number, attendeeId: number, note?: string): Promise<AxiosResponse<WorkshopDetailDTO>> =>
		axiosInstance.put<WorkshopDetailDTO>(`${base(campusId)}/_removeAttendee`, { attendeeId, note });

	/** Multi-día: alta de sesión. 422: salida ≤ entrada, aula de otro plantel, taller anulado/histórico. */
	addSession = async (campusId: number, workshopId: number, body: WorkshopSessionBody): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.post<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessions`, { ...body, workshopId });

	updateSession = async (campusId: number, id: number, body: WorkshopSessionBody): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.put<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessions`, { ...body, id });

	/** Varios días de una vez (v2). */
	addSessions = async (campusId: number, workshopId: number, sessions: WorkshopSessionBody[]): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.post<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessionsBulk`, { workshopId, sessions });

	/** Borra también la asistencia de esa sesión. */
	deleteSession = async (campusId: number, id: number): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.delete<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessions`, { params: { id } });

	/** 422: alumna de baja o de otro taller, taller anulado. */
	setSessionAttendance = async (campusId: number, sessionId: number, attendeeId: number, attended: boolean): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.put<WorkshopMultiDetailDTO>(`${base(campusId)}/_sessionAttendance`, { sessionId, attendeeId, attended });

	/** Forma de pago de la maestra (sección employees) y sus horas del mes. */
	mentorPay = async (campusId: number, userId: number, month?: string): Promise<AxiosResponse<{ config: MentorPayConfigDTO | null; month: MentorPayMonthDTO }>> =>
		axiosInstance.get(`/admin/campus/${campusId}/employees/${userId}/_mentorPay`, { params: month ? { month } : {} });

	saveMentorPay = async (campusId: number, userId: number, body: { payType: "planta" | "hora"; monthlySalary?: number | null; monthlyHoursCap?: number | null; hourlyRate?: number | null }): Promise<AxiosResponse<{ config: MentorPayConfigDTO; month: MentorPayMonthDTO }>> =>
		axiosInstance.put(`/admin/campus/${campusId}/employees/${userId}/_mentorPay`, body);

	/** Módulos Claros: lista por pestaña (por fechas) con el número de cada pestaña. */
	listTab = async (campusId: number, f: { tab: WorkshopTab; q?: string; page?: number; perPage?: number }): Promise<AxiosResponse<PagedDTO<WorkshopRowMultiDTO> & { counts: WorkshopTabCounts }>> =>
		axiosInstance.get(base(campusId), { params: { tab: f.tab, q: f.q || undefined, page: f.page, perPage: f.perPage } });

	/** «Qué Va a Pasar» antes de inscribir, anular o borrar un día con asistencia. No cambia nada. */
	preview = async (campusId: number, workshopId: number, action: WorkshopPreviewDTO["action"], extra: { studentIds?: number[]; charge?: boolean; price?: number; priceNote?: string; sessionId?: number } = {}): Promise<AxiosResponse<WorkshopPreviewDTO>> =>
		axiosInstance.get<WorkshopPreviewDTO>(`${base(campusId)}/_preview`, { params: {
			workshopId, action, studentIds: extra.studentIds ? extra.studentIds.join(",") : undefined,
			charge: extra.charge === undefined ? undefined : (extra.charge ? 1 : 0), price: extra.price, priceNote: extra.priceNote || undefined, sessionId: extra.sessionId
		} });

	/** Anula el taller y sus cobros sin abonos (422 si alguna ya abonó). */
	cancel = async (campusId: number, id: number): Promise<AxiosResponse<WorkshopMultiDetailDTO>> =>
		axiosInstance.put<WorkshopMultiDetailDTO>(`${base(campusId)}/_cancel`, { id });

	/** Los días de taller de la maestra que mira (sección Asistencias). */
	mentorSessions = async (campusId: number): Promise<AxiosResponse<MentorWorkshopSessionsDTO>> =>
		axiosInstance.get<MentorWorkshopSessionsDTO>(`${base(campusId)}/_mentorSessions`);

	mentorAttendance = async (campusId: number, sessionId: number, attendeeId: number, attended: boolean): Promise<AxiosResponse<MentorWorkshopSessionsDTO>> =>
		axiosInstance.put<MentorWorkshopSessionsDTO>(`${base(campusId)}/_mentorAttendance`, { sessionId, attendeeId, attended });

	invitationPreview = async (campusId: number, workshopId: number, courseIds: number[], text?: string): Promise<AxiosResponse<InvitationPreviewDTO>> =>
		axiosInstance.get<InvitationPreviewDTO>(`${base(campusId)}/_invitation`, { params: { workshopId, courseIds: courseIds.join(","), text: text || undefined } });
}

const workshopsService = new WorkshopsService();
export default workshopsService;
